import React, { useState, useEffect, useCallback } from "react";
import moment from "moment";
import "./setHours.css";
import { useNavigate } from "react-router-dom";
import { authenticatedFetch } from "../../network/api";

import NarrowDropdown2 from "../../components/input/new/narrowDropdown2";
import IconCheckbox from "../../components/input/new/iconCheckbox";
import Header from "../components/header";
import { useSelector } from "react-redux";

const DAYS = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

const TIMES = Array.from({ length: 13 * 4 }, (_, idx) => {
  const hours = Math.floor(idx / 4) + 8; // Start at 8 AM
  const minutes = (idx % 4) * 15;

  // Build a moment at today's date, with the desired hour/minute
  const t = moment().hour(hours).minute(minutes).second(0);

  // e.g. "8:00 AM", "8:15 AM", … "9:00 PM"
  const label = t.format("h:mm A");

  return { label, value: label };
});

const LOCATIONS = [
  { label: "Office (Video Visit)", value: "video" },
  { label: "Office (In‑person Visit)", value: "inPerson" },
];

const RECURRENCE = [
  { label: "Every week", value: "weekly" },
  { label: "Every other week", value: "biweekly" },
  { label: "Every month", value: "monthly" },
];

const parseTime = (str) => {
  const [time, meridiem] = str.split(" ");
  let [h, m] = time.split(":").map(Number);
  if (meridiem === "PM" && h !== 12) h += 12;
  if (meridiem === "AM" && h === 12) h = 0;
  return h * 60 + m;
};

const validateDaySlots = (slots) => {
  const arr = slots
    .filter((s) => s.start && s.end)
    .map((s) => ({ start: parseTime(s.start), end: parseTime(s.end) }))
    .sort((a, b) => a.start - b.start);
  for (let i = 1; i < arr.length; i++) {
    if (arr[i].start < arr[i - 1].end) {
      return "Time slots cannot overlap";
    }
  }
  return "";
};

export default function SetHours() {
  const navigate = useNavigate();
  const userId = useSelector(
    (state) => state.attorneyData?.attorneyData?.user_id
  );
  const [days, setDays] = useState(
    DAYS.map((name) => ({ name, enabled: false, slots: [] }))
  );
  const [errors, setErrors] = useState({});
  const [dirty, setDirty] = useState(false);

  // load initial schedule
  const fetchSchedule = useCallback(async () => {
    try {
      const res = await authenticatedFetch(
        `/api/attorney/webapp/calendar/recurringSchedules?user_id=${userId}`
      );
      const { schedules } = await res.json();
      // map schedules into days
      const byDay = DAYS.map((_, di) => ({
        name: DAYS[di],
        enabled: false,
        slots: [],
      }));

      schedules.forEach((s) => {
        const di = s.day_of_week;
        // parse recurrence from rrule
        let recurrence = "weekly";
        const m = s.rrule.match(/INTERVAL=(\d+)/);
        const interval = m ? parseInt(m[1], 10) : 1;
        if (s.rrule.startsWith("FREQ=MONTHLY")) recurrence = "monthly";
        else if (interval === 2) recurrence = "biweekly";

        byDay[di].enabled = true;
        byDay[di].slots.push({
          // parse as UTC then convert to local before formatting
          start: moment.utc(s.start_time, "HH:mm:ss").local().format("h:mm A"),
          end: moment.utc(s.end_time, "HH:mm:ss").local().format("h:mm A"),
          locations: s.location_types || [],
          recurrence,
        });
      });

      setDays(byDay);
    } catch (err) {
      console.error("Failed to load schedule:", err);
    }
  }, [userId]);

  useEffect(() => {
    if (userId) fetchSchedule();
  }, [userId, fetchSchedule]);

  const markDirty = () => {
    if (!dirty) setDirty(true);
  };

  const toggleDay = (di) => {
    markDirty();
    setDays((ds) =>
      ds.map((d, i) => {
        if (i !== di) return d;
        const willEnable = !d.enabled;
        const newSlots =
          willEnable && d.slots.length === 0
            ? [{ start: "", end: "", locations: [], recurrence: "weekly" }]
            : d.slots;
        return { ...d, enabled: willEnable, slots: newSlots };
      })
    );
    setErrors((e) => ({ ...e, [di]: "" }));
  };

  const updateSlot = (di, si, field, value) => {
    markDirty();
    const newSlots = days[di].slots.map((slot, idx) =>
      idx === si ? { ...slot, [field]: value } : slot
    );
    setErrors((e) => ({ ...e, [di]: validateDaySlots(newSlots) }));
    setDays((ds) =>
      ds.map((d, i) => (i === di ? { ...d, slots: newSlots } : d))
    );
  };

  const addSlot = (di) => {
    markDirty();
    const newSlots = [
      ...days[di].slots,
      { start: "", end: "", locations: [], recurrence: "weekly" },
    ];
    setErrors((e) => ({ ...e, [di]: validateDaySlots(newSlots) }));
    setDays((ds) =>
      ds.map((d, i) =>
        i === di ? { ...d, slots: newSlots, enabled: true } : d
      )
    );
  };

  const deleteSlot = (di, si) => {
    markDirty();
    const newSlots = days[di].slots.filter((_, idx) => idx !== si);
    const willDisable = newSlots.length === 0;
    setErrors((e) => ({ ...e, [di]: "" }));
    setDays((ds) =>
      ds.map((d, i) =>
        i === di
          ? { ...d, slots: newSlots, enabled: willDisable ? false : d.enabled }
          : d
      )
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // validation same as before…
    const newErrors = {};
    days.forEach((d, di) => {
      if (d.enabled) {
        d.slots.forEach((slot) => {
          if (!slot.start || !slot.end) {
            newErrors[di] =
              "Please fill in both start and end times for all slots.";
          }
        });
      }
    });
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    const overlapErrors = {};
    days.forEach((d, di) => {
      const msg = validateDaySlots(d.slots);
      if (msg) overlapErrors[di] = msg;
    });
    if (Object.keys(overlapErrors).length > 0) {
      setErrors(overlapErrors);
      return;
    }

    const slotsPayload = days.flatMap((d, di) =>
      d.enabled
        ? d.slots.map((slot) => {
            // parse local time, shift to UTC, then format as HH:mm:ss
            const startUtc = moment(slot.start, "h:mm A")
              .utc()
              .format("HH:mm:ss");
            const endUtc = moment(slot.end, "h:mm A").utc().format("HH:mm:ss");

            return {
              day_of_week: di,
              start_time: startUtc,
              end_time: endUtc,
              recurrence_type: slot.recurrence,
              interval: slot.recurrence === "biweekly" ? 2 : 1,
              start_date: null,
              end_date: null,
              location_types: slot.locations,
            };
          })
        : []
    );

    try {
      const res = await authenticatedFetch(
        `/api/attorney/webapp/calendar/recurringSchedules?user_id=${userId}`,
        {
          method: "POST",
          body: JSON.stringify({ slots: slotsPayload, initial: true }),
        }
      );
      if (!res.ok) throw new Error(await res.text());
      alert("Schedule saved successfully.");
      setDirty(false);
      // reload to pick up server‑generated RRULE etc.
      await fetchSchedule();
      navigate(`/attorney/${userId}/home`);
    } catch (err) {
      console.error("Save failed:", err);
      alert("Failed to save schedule: " + err.message);
    }
  };

  return (
    <div className="set-hours-root">
      <Header />
      <form className="smh-container" onSubmit={handleSubmit}>
        <h1 className="smh-title">Set Up Your Client Meeting Hours</h1>
        <p className="smh-subtitle">
          Review or adjust your available hours for client meetings. Once set,
          clients can book appointments during these times.
        </p>

        {days.map((day, di) => (
          <div key={day.name} className="smh-day-row">
            <IconCheckbox
              className="smh-day-checkbox"
              checked={day.enabled}
              onChange={() => toggleDay(di)}
              label={day.name}
            />
            <div
              className={`smh-slots-container ${
                !day.enabled ? "smh-disabled" : ""
              }`}
            >
              {day.slots.map((slot, si) => (
                <div key={si} className="smh-slot-row">
                  <NarrowDropdown2
                    placeholder="Start"
                    items={TIMES}
                    value={slot.start}
                    onChange={(v) => updateSlot(di, si, "start", v)}
                  />
                  <span className="smh-dash">–</span>
                  <NarrowDropdown2
                    placeholder="End"
                    items={TIMES}
                    value={slot.end}
                    onChange={(v) => updateSlot(di, si, "end", v)}
                  />
                  <NarrowDropdown2
                    placeholder="Select location"
                    items={LOCATIONS}
                    multiple
                    value={slot.locations}
                    onChange={(v) => updateSlot(di, si, "locations", v)}
                  />
                  <NarrowDropdown2
                    placeholder="Frequency"
                    items={RECURRENCE}
                    value={slot.recurrence}
                    onChange={(v) => updateSlot(di, si, "recurrence", v)}
                  />
                  <button
                    type="button"
                    className="smh-delete-slot"
                    onClick={() => deleteSlot(di, si)}
                  >
                    Delete
                  </button>
                </div>
              ))}

              {day.enabled && (
                <button
                  type="button"
                  className="smh-add-slot"
                  onClick={() => addSlot(di)}
                >
                  Add another
                </button>
              )}

              {errors[di] && <div className="smh-error-msg">{errors[di]}</div>}
            </div>
          </div>
        ))}

        <div className="smh-footer">
          <p className="help-text">Need help?</p>
          <button type="submit" className="appt-confirm-btn" disabled={!dirty}>
            Submit
          </button>
        </div>
      </form>
    </div>
  );
}
